import type { GameStateManager } from '../../game/GameState';
import type { ResearchCompletionModal } from '../ResearchCompletionModal';
import type { BreachEscapeModal } from '../BreachEscapeModal';
import type { BreachContainmentModal } from '../BreachContainmentModal';
import type { AnomalyReceivedModal } from '../AnomalyReceivedModal';
import type { InsanityModal } from '../InsanityModal';
import type { RealityBreachModal } from '../RealityBreachModal';
import type { RealityKillModal } from '../RealityKillModal';
import type { WinModal } from '../WinModal';
import type { LoseModal } from '../LoseModal';
import type { ToastManager } from '../ToastManager';

export class PendingStateHandler {
  private isModalShowing: boolean = false;

  constructor(
    private gameState: GameStateManager,
    private researchCompletionModal: ResearchCompletionModal,
    private breachEscapeModal: BreachEscapeModal,
    private breachContainmentModal: BreachContainmentModal,
    private anomalyReceivedModal: AnomalyReceivedModal,
    private insanityModal: InsanityModal,
    private realityBreachModal: RealityBreachModal | null,
    private realityKillModal: RealityKillModal,
    private winModal: WinModal,
    private loseModal: LoseModal,
    private toastManager: ToastManager
  ) {}

  notifyModalClosed(): void {
    this.isModalShowing = false;
  }

  checkAndShowPendingModals(): void {
    // Check if we are locked by a showing modal
    if (this.isModalShowing) {
      return;
    }
    
    // Helper to check if any modal is currently open (fallback/double-check)
    const isAnyModalOpen = (): boolean => {
      return this.researchCompletionModal.isOpen() ||
             this.breachEscapeModal.isOpen() ||
             this.breachContainmentModal.isOpen() ||
             this.anomalyReceivedModal.isOpen() ||
             this.insanityModal.isOpen() ||
             (this.realityBreachModal?.isOpen() || false) ||
             this.realityKillModal.isOpen() ||
             this.winModal.isOpen() ||
             this.loseModal.isOpen();
    };
    
    // Only show one modal at a time - check in priority order
    if (!isAnyModalOpen()) {
      // Check for pending modals in priority order
      const pendingResearch = this.gameState.getPendingResearchCompletion();
      if (pendingResearch) {
        this.isModalShowing = true;
        this.researchCompletionModal.show(pendingResearch);
        return;
      }
      
      const pendingContainment = this.gameState.getPendingContainmentCompletion();
      if (pendingContainment) {
        this.isModalShowing = true;
        this.breachContainmentModal.show(pendingContainment);
        return;
      }
      
      const pendingBreachEscape = this.gameState.getPendingBreachEscape();
      if (pendingBreachEscape) {
        this.isModalShowing = true;
        this.breachEscapeModal.show(pendingBreachEscape);
        return;
      }
      
      const pendingAnomalyReceived = this.gameState.getPendingAnomalyReceived();
      if (pendingAnomalyReceived) {
        this.isModalShowing = true;
        this.anomalyReceivedModal.show(pendingAnomalyReceived);
        return;
      }
      
      const pendingInsanity = this.gameState.getPendingInsanity();
      if (pendingInsanity) {
        this.isModalShowing = true;
        this.insanityModal.show(pendingInsanity);
        return;
      }
      
      const pendingRealityBreach = this.gameState.getPendingRealityBreach();
      if (pendingRealityBreach && this.realityBreachModal) {
        this.isModalShowing = true;
        this.realityBreachModal.show(pendingRealityBreach);
        return;
      }
      
      const pendingRealityKill = this.gameState.getPendingRealityKill();
      if (pendingRealityKill) {
        this.isModalShowing = true;
        this.realityKillModal.show(pendingRealityKill);
        return;
      }
    }
    
    // Process pending toasts (always process these)
    const pendingToasts = this.gameState.getPendingToasts();
    if (pendingToasts.length > 0) {
      pendingToasts.forEach(toast => {
        this.toastManager.show(toast);
      });
      this.gameState.clearPendingToasts();
    }
    
    this.toastManager.update();
    
    // Check for game end state last (lowest priority) - show after all explanatory modals
    // Only show if no other modals are open
    if (!isAnyModalOpen()) {
      const gameEndState = this.gameState.getGameEndState();
      if (gameEndState) {
        if (gameEndState.type === 'victory' && !this.winModal.isOpen()) {
          this.isModalShowing = true;
          this.winModal.show(gameEndState);
          return;
        } else if (gameEndState.type === 'defeat' && !this.loseModal.isOpen()) {
          this.isModalShowing = true;
          this.loseModal.show(gameEndState);
          return;
        }
      }
    }
  }
}
